// src/context/courseStore.js
//
// REESCRITO — ya no usa datos estáticos en memoria (../data/courses).
// Ahora es un caché local respaldado por el backend real (cursoService).
//
// CAMBIO DE CONTRATO IMPORTANTE: las acciones de escritura (createCourse,
// updateCourse, deleteCourse, addUnit, addContent...) ahora son ASYNC.
// Cualquier componente que las llamaba de forma síncrona (`const r =
// createCourse(data)`) debe cambiar a `const r = await createCourse(data)`.
//
// Los getters de lectura (getAllCourses, getCourseById, getCourseBySlug)
// siguen siendo síncronos — leen del caché local (`courses`), que se
// llena llamando a `fetchCourses()` (normalmente en un useEffect al
// montar la página).

import { create } from 'zustand';
import { cursoService } from '../services/api';

const useCourseStore = create((set, get) => ({
  courses: {},         // cache local: { [id]: cursoDetalle }
  categories: [],
  loading: false,
  error: null,

  // ── Lectura (síncrona, sobre el caché local) ─────────────────────
  getAllCourses: () => Object.values(get().courses),

  getCourseById: (courseId) => get().courses[courseId],

  getCourseBySlug: (slug) => Object.values(get().courses).find((c) => c.slug === slug),

  // ── Carga desde el backend ────────────────────────────────────────
  fetchCourses: async (params = {}) => {
    set({ loading: true, error: null });
    try {      const result = await cursoService.getAll(params);
      const byId = {};
      result.data.forEach((c) => { byId[c.id] = c; });
      set({ courses: byId, loading: false });
      return result;
    } catch (err) {
      set({ loading: false, error: err.message });
      return { data: [], total: 0 };
    }
  },

  // Trae el detalle completo (con unidades y contenidos) y lo mezcla
  // en el caché — úsalo antes de entrar a CourseView/CourseDetail.
  fetchCourseDetail: async (courseId) => {
    set({ loading: true, error: null });
    try {
      const course = await cursoService.getById(courseId);
      set((state) => ({
        courses: { ...state.courses, [courseId]: course },
        loading: false,
      }));
      return course;
    } catch (err) {
      set({ loading: false, error: err.message });
      return null;
    }
  },

  fetchCategories: async () => {
    try {
      const { catalogoService } = await import('../services/api');
      const categories = await catalogoService.getCategorias();
      set({ categories });
      return categories;
    } catch (err) {
      set({ error: err.message });
      return [];
    }
  },

  // ── Escritura (async — llaman al backend, luego refrescan el caché) ─
  createCourse: async (courseData) => {
    try {
      const created = await cursoService.create(courseData);
      const course = await cursoService.getById(created.id);
      set((state) => ({ courses: { ...state.courses, [course.id]: course } }));
      return { success: true, course };
    } catch (err) {
      return { success: false, error: err.message };
    }
  },

  updateCourse: async (courseId, updates) => {
    try {
      await cursoService.update(courseId, updates);
      const course = await cursoService.getById(courseId);
      set((state) => ({ courses: { ...state.courses, [courseId]: course } }));
      return { success: true, course };
    } catch (err) {
      return { success: false, error: err.message };
    }
  },

  // El backend no expone DELETE de curso todavía (solo publicar/despublicar
  // vía toggle) — se mapea a "despublicar" como equivalente más cercano.
  deleteCourse: async (courseId) => {
    try {
      const course = get().courses[courseId];
      if (course?.published) await cursoService.publicar(courseId);
      set((state) => {
        const { [courseId]: _removed, ...rest } = state.courses;
        return { courses: rest };
      });
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  },

  togglePublish: async (courseId) => {
    try {
      const result = await cursoService.publicar(courseId);
      const course = await cursoService.getById(courseId);
      set((state) => ({ courses: { ...state.courses, [courseId]: course } }));
      return { success: true, published: result.publicado, course };
    } catch (err) {
      return { success: false, error: err.message };
    }
  },

  // ── Unidades ──────────────────────────────────────────────────────
  addUnit: async (courseId, unitData) => {
    try {
      await cursoService.createUnidad(courseId, unitData);
      const course = await cursoService.getById(courseId);
      set((state) => ({ courses: { ...state.courses, [courseId]: course } }));
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  },

  updateUnit: async (courseId, unitId, updates) => {
    try {
      await cursoService.updateUnidad(courseId, unitId, updates);
      const course = await cursoService.getById(courseId);
      set((state) => ({ courses: { ...state.courses, [courseId]: course } }));
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  },

  // Soft delete en backend (marca Activa=false y los contenidos de la unidad).
  deleteUnit: async (courseId, unitId) => {
    try {
      await cursoService.deleteUnidad(courseId, unitId);
      const course = await cursoService.getById(courseId);
      set((state) => ({ courses: { ...state.courses, [courseId]: course } }));
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  },

  // ── Contenidos ────────────────────────────────────────────────────
  addContent: async (courseId, unitId, contentData) => {
    try {
      await cursoService.createContenido(courseId, unitId, contentData);
      const course = await cursoService.getById(courseId);
      set((state) => ({ courses: { ...state.courses, [courseId]: course } }));
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  },

  updateContent: async (courseId, unitId, contentId, contentData) => {
    try {
      await cursoService.updateContenido(courseId, unitId, contentId, contentData);
      const course = await cursoService.getById(courseId);
      set((state) => ({ courses: { ...state.courses, [courseId]: course } }));
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  },

  deleteContent: async (courseId, unitId, contentId) => {
    try {
      await cursoService.deleteContenido(courseId, unitId, contentId);
      const course = await cursoService.getById(courseId);
      set((state) => ({ courses: { ...state.courses, [courseId]: course } }));
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  },

  // ── Estadísticas ──────────────────────────────────────────────────
  getCourseStats: () => {
    const allCourses = Object.values(get().courses);
    const categories = get().categories;
    return {
      totalCourses: allCourses.length,
      totalStudents: allCourses.reduce((sum, c) => sum + (c.totalEnrollments || 0), 0),
      byCategory: categories.map((cat) => ({
        ...cat,
        count: allCourses.filter((c) => c.category === cat.name).length,
      })),
    };
  },
}));

export default useCourseStore;
